import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Copy, Check, Upload, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';

function CircularProgress({ percent, size = 80 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = (percent / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="url(#progressGrad)" strokeWidth="6"
        strokeDasharray={`${strokeDash} ${circumference}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      <defs>
        <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00E5FF" />
          <stop offset="100%" stopColor="#7B2FBE" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Step4Checklist({ partnerProfile, partnerUser, stepsDone, onLaunch }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [confetti, setConfetti] = useState(false);

  const passwordDone = stepsDone?.includes(1);
  const profileDone = stepsDone?.includes(2);
  const productDone = stepsDone?.includes(3);

  const [checklist, setChecklist] = useState([
    { id: 1, label: 'Password updated', done: passwordDone, required: true, action: null, actionLabel: null },
    { id: 2, label: 'Company profile completed', done: profileDone, required: true, action: null, actionLabel: null },
    { id: 3, label: 'First product added', done: productDone, required: false, action: () => navigate('/partner-onboarding-wizard?step=3'), actionLabel: 'Add Now' },
    { id: 4, label: 'Upload your product catalog (CSV)', done: false, required: false, action: () => setCsvModalOpen(true), actionLabel: 'Upload Now' },
    { id: 5, label: 'Set up your first promotion', done: false, required: false, action: () => navigate('/retail-partner-portal'), actionLabel: 'Create Promotion' },
    { id: 6, label: 'Review scan analytics setup', done: false, required: false, action: () => navigate('/retail-partner-portal'), actionLabel: 'View Analytics' },
  ]);

  const completedCount = checklist?.filter(c => c?.done)?.length;
  const completionPercent = Math.round((completedCount / checklist?.length) * 100);
  const allDone = completedCount === checklist?.length;

  useEffect(() => {
    if (allDone) setConfetti(true);
  }, [allDone]);

  const maskedApiKey = partnerProfile?.api_key
    ? partnerProfile?.api_key?.substring(0, 8) + '••••••••••••••••••••••••' :'N/A';

  const handleCopyKey = () => {
    if (partnerProfile?.api_key) {
      navigator.clipboard?.writeText(partnerProfile?.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLaunch = async () => {
    setError('');
    setLaunching(true);
    try {
      const { error: updateErr } = await supabase
        ?.from('partner_profiles')
        ?.update({
          onboarding_completed: true,
          onboarding_completed_at: new Date()?.toISOString(),
          onboarding_step: 4,
        })
        ?.eq('id', partnerProfile?.id);

      if (updateErr) { setError(updateErr?.message); return; }

      // Trigger welcome email (fire and forget)
      supabase?.functions?.invoke('partner-welcome-email', {
        body: {
          company_name: partnerProfile?.business_name,
          contact_first_name: partnerProfile?.business_name?.split(' ')?.[0] ?? 'Partner',
          contact_email: partnerProfile?.contact_email ?? partnerUser?.email,
          api_key: partnerProfile?.api_key,
          partner_tier: partnerProfile?.partner_tier,
        },
      })?.catch(() => {});

      onLaunch?.();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      {/* Confetti burst */}
      {confetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 30 })?.map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-sm animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                background: ['#00E5FF', '#7B2FBE', '#10B981', '#F59E0B']?.[i % 4],
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${0.8 + Math.random() * 0.6}s`,
                opacity: 0.8,
              }}
            />
          ))}
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>You're almost ready! ✨</h1>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Complete remaining steps anytime from your portal settings.</p>
      </div>
      {/* Progress ring + score */}
      <div
        className="flex items-center gap-6 p-5 rounded-2xl mb-6"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,229,255,0.1)' }}
      >
        <div className="relative flex-shrink-0">
          <CircularProgress percent={completionPercent} size={80} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{completionPercent}%</span>
          </div>
        </div>
        <div>
          <p className="text-base font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Your profile is {completionPercent}% complete</p>
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{completedCount} of {checklist?.length} tasks done</p>
          {allDone && (
            <span
              className="inline-block text-xs font-bold px-3 py-1 rounded-full mt-2"
              style={{ background: 'rgba(0,229,255,0.15)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.3)', fontFamily: 'DM Sans, sans-serif' }}
            >
              Partner Ready 🏆
            </span>
          )}
        </div>
      </div>
      {/* Checklist */}
      <div className="space-y-2 mb-6">
        {checklist?.map((item) => (
          <div
            key={item?.id}
            className="flex items-center gap-3 p-4 rounded-xl transition-all"
            style={{
              background: item?.done ? 'rgba(0,229,255,0.04)' : 'rgba(255,255,255,0.02)',
              border: item?.done ? '1px solid rgba(0,229,255,0.15)' : '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {item?.done ? (
              <CheckCircle size={18} style={{ color: '#00E5FF', flexShrink: 0 }} />
            ) : (
              <Circle size={18} style={{ color: '#334155', flexShrink: 0 }} />
            )}
            <span
              className="flex-1 text-sm"
              style={{
                color: item?.done ? '#E2E8F0' : '#64748B',
                fontFamily: 'DM Sans, sans-serif',
                textDecoration: item?.done ? 'none' : 'none',
              }}
            >
              {item?.label}
            </span>
            <span
              className="text-xs font-medium px-2.5 py-1 rounded-full"
              style={{
                background: item?.done ? 'rgba(0,229,255,0.1)' : 'rgba(255,255,255,0.05)',
                color: item?.done ? '#00E5FF' : '#475569',
                border: item?.done ? '1px solid rgba(0,229,255,0.2)' : '1px solid rgba(255,255,255,0.06)',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {item?.done ? 'Complete' : 'Pending'}
            </span>
            {!item?.done && item?.action && (
              <button
                onClick={item?.action}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background: 'rgba(0,229,255,0.1)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.2)', fontFamily: 'DM Sans, sans-serif' }}
              >
                {item?.actionLabel}
              </button>
            )}
          </div>
        ))}
      </div>
      {/* API Key card */}
      <div
        className="p-5 rounded-2xl mb-6"
        style={{ background: 'rgba(123,47,190,0.06)', border: '1px solid rgba(123,47,190,0.2)' }}
      >
        <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#7B2FBE', fontFamily: 'DM Sans, sans-serif' }}>Your API Key</p>
        <div className="flex items-center gap-3">
          <code className="flex-1 text-sm font-mono" style={{ color: '#94A3B8' }}>{maskedApiKey}</code>
          <button
            onClick={handleCopyKey}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
            style={{
              background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(123,47,190,0.15)',
              color: copied ? '#10B981' : '#7B2FBE',
              border: copied ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(123,47,190,0.3)',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-xs mt-2" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Use this key to connect your systems to FinVisAR. Keep it secure.</p>
      </div>
      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl mb-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
          <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
        </div>
      )}
      {/* Launch button */}
      <button
        onClick={handleLaunch}
        disabled={launching}
        className="w-full py-4 rounded-xl text-base font-bold text-white flex items-center justify-center gap-2"
        style={{
          background: launching ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00E5FF 0%, #7B2FBE 100%)',
          fontFamily: 'DM Sans, sans-serif',
          opacity: launching ? 0.7 : 1,
          boxShadow: launching ? 'none' : '0 4px 24px rgba(0,229,255,0.25)',
        }}
      >
        {launching && <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
        {launching ? 'Launching Portal...' : '🚀 Launch My Partner Portal'}
      </button>
      {/* CSV Upload Modal */}
      {csvModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)' }}>
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>Upload Product Catalog</h3>
            <p className="text-sm mb-4" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Upload a CSV file with your product catalog. You can do this anytime from your portal.</p>
            <div
              className="flex flex-col items-center justify-center p-8 rounded-xl cursor-pointer"
              style={{ border: '2px dashed rgba(0,229,255,0.25)', background: 'rgba(0,229,255,0.02)' }}
            >
              <Upload size={28} style={{ color: '#00E5FF' }} />
              <p className="text-sm font-medium mt-2" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Drop CSV file here or click to browse</p>
              <p className="text-xs mt-1" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>CSV format: name, sku, category, price, description</p>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setCsvModalOpen(false)} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Close</button>
              <button
                onClick={() => {
                  setChecklist(prev => prev?.map(c => c?.id === 4 ? { ...c, done: true } : c));
                  setCsvModalOpen(false);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}
              >
                Mark as Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
